import { FormEvent, useEffect, useState } from "react";
import { PackageCheck, ArrowLeftRight, History } from "lucide-react";
import { toast } from "sonner";
import { httpServices as services } from "@/services";
import type { InventoryBalance, InventoryLocation, StockMovement, InputCatalogueItem } from "@/types/domain";
import { PageHeader } from "@/components/PageHeader";
import { LoadingState } from "@/components/shared/StatePanels";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

type Tab = "balances" | "movements";

export default function InventoryPage() {
  const [tab, setTab] = useState<Tab>("balances");
  const [balances, setBalances] = useState<InventoryBalance[]>();
  const [movements, setMovements] = useState<StockMovement[]>();
  const [locations, setLocations] = useState<InventoryLocation[]>();
  const [catalogue, setCatalogue] = useState<InputCatalogueItem[]>();
  const [receiving, setReceiving] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [form, setForm] = useState({ item: "", location: "", quantity: "", lot: "", supplier: "", notes: "", toLocation: "" });

  useEffect(() => {
    services.inventory.listBalances().then((r) => setBalances(r.items));
    services.inventory.listMovements().then((r) => setMovements(r.items));
    services.inventory.listLocations().then((r) => setLocations(r.items));
    services.inputs.listCatalogue().then((r) => setCatalogue(r.items));
  }, []);

  const refresh = async () => {
    setBalances(undefined);
    setMovements(undefined);
    setBalances((await services.inventory.listBalances()).items);
    setMovements((await services.inventory.listMovements()).items);
  };

  const set = (key: keyof typeof form, value: string) => setForm({ ...form, [key]: value });

  const submitReceipt = async (event: FormEvent) => {
    event.preventDefault();
    if (!form.item || !form.location || !form.quantity || Number(form.quantity) <= 0) {
      toast.error("Item, location and quantity are required");
      return;
    }
    try {
      await services.inventory.createReceipt({
        itemId: form.item,
        inventoryLocationId: form.location,
        lotNumber: form.lot || undefined,
        quantity: Number(form.quantity),
        unit: catalogue?.find((c) => c.id === form.item)?.unit || "units",
        supplierName: form.supplier || undefined,
        notes: form.notes || undefined,
      });
      toast.success("Stock received", { description: `${form.quantity} units added to inventory.` });
      setReceiving(false);
      setForm({ item: "", location: "", quantity: "", lot: "", supplier: "", notes: "", toLocation: "" });
      refresh();
    } catch (e) {
      toast.error("Receipt failed", { description: (e as Error).message });
    }
  };

  const submitIssue = async (event: FormEvent) => {
    event.preventDefault();
    if (!form.item || !form.location || !form.quantity || Number(form.quantity) <= 0) {
      toast.error("Item, location and quantity are required");
      return;
    }
    try {
      await services.inventory.createIssue({
        itemId: form.item,
        inventoryLocationId: form.location,
        quantity: Number(form.quantity),
        unit: catalogue?.find((c) => c.id === form.item)?.unit || "units",
        notes: form.notes || undefined,
      });
      toast.success("Stock issued", { description: `${form.quantity} units issued from inventory.` });
      setIssuing(false);
      setForm({ item: "", location: "", quantity: "", lot: "", supplier: "", notes: "", toLocation: "" });
      refresh();
    } catch (e) {
      toast.error("Issue failed", { description: (e as Error).message });
    }
  };

  const submitTransfer = async (event: FormEvent) => {
    event.preventDefault();
    if (!form.item || !form.location || !form.toLocation || !form.quantity || Number(form.quantity) <= 0) {
      toast.error("Item, source location, destination and quantity are required");
      return;
    }
    try {
      await services.inventory.createTransfer({
        itemId: form.item,
        fromLocationId: form.location,
        toLocationId: form.toLocation,
        quantity: Number(form.quantity),
        unit: catalogue?.find((c) => c.id === form.item)?.unit || "units",
        notes: form.notes || undefined,
      });
      toast.success("Transfer complete", { description: `${form.quantity} units transferred.` });
      setTransferring(false);
      setForm({ item: "", location: "", quantity: "", lot: "", supplier: "", notes: "", toLocation: "" });
      refresh();
    } catch (e) {
      toast.error("Transfer failed", { description: (e as Error).message });
    }
  };

  const isLoading = !balances || !movements || !locations || !catalogue;
  if (isLoading) return <LoadingState label="Loading inventory" />;

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <PageHeader
        eyebrow="Inventory & Inputs"
        title="Stock Management"
        description="Receive, issue and transfer stock. View current balances and transaction history."
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setReceiving((v) => !v); setIssuing(false); setTransferring(false); }} data-testid="inventory-receive-button">
              <PackageCheck className="mr-2 size-4" />Receive
            </Button>
            <Button variant="outline" onClick={() => { setIssuing((v) => !v); setReceiving(false); setTransferring(false); }} data-testid="inventory-issue-button">
              <ArrowLeftRight className="mr-2 size-4" />Issue
            </Button>
            <Button variant="outline" onClick={() => { setTransferring((v) => !v); setReceiving(false); setIssuing(false); }} data-testid="inventory-transfer-button">
              <ArrowLeftRight className="mr-2 size-4" />Transfer
            </Button>
          </div>
        }
      />

      {/* Action panels */}
      {receiving && (
        <Card data-testid="inventory-receive-panel">
          <CardContent className="p-5">
            <form onSubmit={submitReceipt} className="space-y-4" data-testid="inventory-receive-form">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>Input item</Label>
                  <Select value={form.item} onValueChange={(v: string) => set("item", v)}>
                    <SelectTrigger data-testid="receive-item-select"><SelectValue placeholder="Select item" /></SelectTrigger>
                    <SelectContent>
                      {catalogue.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Select value={form.location} onValueChange={(v: string) => set("location", v)}>
                    <SelectTrigger data-testid="receive-location-select"><SelectValue placeholder="Select location" /></SelectTrigger>
                    <SelectContent>
                      {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.locationName}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="receive-quantity">Quantity</Label>
                  <Input id="receive-quantity" type="number" min="0" value={form.quantity} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("quantity", e.target.value)} data-testid="receive-quantity-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="receive-lot">Lot number</Label>
                  <Input id="receive-lot" value={form.lot} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("lot", e.target.value)} data-testid="receive-lot-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="receive-supplier">Supplier</Label>
                  <Input id="receive-supplier" value={form.supplier} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("supplier", e.target.value)} data-testid="receive-supplier-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="receive-notes">Notes</Label>
                  <Input id="receive-notes" value={form.notes} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("notes", e.target.value)} data-testid="receive-notes-input" />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setReceiving(false)} data-testid="receive-cancel">Cancel</Button>
                <Button type="submit" data-testid="receive-submit">Receive stock</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {issuing && (
        <Card data-testid="inventory-issue-panel">
          <CardContent className="p-5">
            <form onSubmit={submitIssue} className="space-y-4" data-testid="inventory-issue-form">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>Input item</Label>
                  <Select value={form.item} onValueChange={(v: string) => set("item", v)}>
                    <SelectTrigger data-testid="issue-item-select"><SelectValue placeholder="Select item" /></SelectTrigger>
                    <SelectContent>
                      {catalogue.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Select value={form.location} onValueChange={(v: string) => set("location", v)}>
                    <SelectTrigger data-testid="issue-location-select"><SelectValue placeholder="Select location" /></SelectTrigger>
                    <SelectContent>
                      {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.locationName}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="issue-quantity">Quantity</Label>
                  <Input id="issue-quantity" type="number" min="0" value={form.quantity} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("quantity", e.target.value)} data-testid="issue-quantity-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="issue-notes">Notes</Label>
                  <Input id="issue-notes" value={form.notes} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("notes", e.target.value)} data-testid="issue-notes-input" />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIssuing(false)} data-testid="issue-cancel">Cancel</Button>
                <Button type="submit" data-testid="issue-submit">Issue stock</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {transferring && (
        <Card data-testid="inventory-transfer-panel">
          <CardContent className="p-5">
            <form onSubmit={submitTransfer} className="space-y-4" data-testid="inventory-transfer-form">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>Input item</Label>
                  <Select value={form.item} onValueChange={(v: string) => set("item", v)}>
                    <SelectTrigger data-testid="transfer-item-select"><SelectValue placeholder="Select item" /></SelectTrigger>
                    <SelectContent>
                      {catalogue.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>From location</Label>
                  <Select value={form.location} onValueChange={(v: string) => set("location", v)}>
                    <SelectTrigger data-testid="transfer-from-select"><SelectValue placeholder="Select source" /></SelectTrigger>
                    <SelectContent>
                      {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.locationName}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>To location</Label>
                  <Select value={form.toLocation} onValueChange={(v: string) => set("toLocation", v)}>
                    <SelectTrigger data-testid="transfer-to-select"><SelectValue placeholder="Select destination" /></SelectTrigger>
                    <SelectContent>
                      {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.locationName}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="transfer-quantity">Quantity</Label>
                  <Input id="transfer-quantity" type="number" min="0" value={form.quantity} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("quantity", e.target.value)} data-testid="transfer-quantity-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="transfer-notes">Notes</Label>
                  <Input id="transfer-notes" value={form.notes} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set("notes", e.target.value)} data-testid="transfer-notes-input" />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setTransferring(false)} data-testid="transfer-cancel">Cancel</Button>
                <Button type="submit" data-testid="transfer-submit">Transfer stock</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Tab switcher */}
      <div className="flex gap-2 border-b">
        <button
          className={`pb-2 px-4 text-sm font-medium border-b-2 transition-colors ${tab === "balances" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          onClick={() => setTab("balances")}
          data-testid="inventory-tab-balances"
        >
          Current Stock
        </button>
        <button
          className={`pb-2 px-4 text-sm font-medium border-b-2 transition-colors ${tab === "movements" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          onClick={() => setTab("movements")}
          data-testid="inventory-tab-movements"
        >
          <History className="inline mr-1 size-4" />History
        </button>
      </div>

      {/* Balances table */}
      {tab === "balances" && (
        <Card>
          <CardContent className="p-0">
            <Table data-testid="inventory-balances-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead className="text-right">Qty on hand</TableHead>
                  <TableHead>Unit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {balances.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground py-6">No stock balances</TableCell>
                  </TableRow>
                ) : balances.map((b) => (
                  <TableRow key={`${b.itemId}-${b.locationId}`} data-testid={`balance-row-${b.itemId}`}>
                    <TableCell>
                      <p className="font-semibold">{b.itemName}</p>
                      <p className="font-mono text-xs text-muted-foreground">{b.itemCode}</p>
                    </TableCell>
                    <TableCell>{b.locationName}</TableCell>
                    <TableCell className="text-right font-mono">{b.quantityOnHand.toLocaleString()}</TableCell>
                    <TableCell>{b.unit}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Movements history */}
      {tab === "movements" && (
        <Card>
          <CardContent className="p-0">
            <Table data-testid="inventory-movements-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Reference</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Item</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {movements.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-6">No movements recorded</TableCell>
                  </TableRow>
                ) : movements.map((m) => (
                  <TableRow key={m.id} data-testid={`movement-row-${m.id}`}>
                    <TableCell className="font-mono text-xs">{m.reference}</TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${m.movementType === "RECEIPT" ? "bg-green-100 text-green-800" : m.movementType === "ISSUE" ? "bg-orange-100 text-orange-800" : "bg-blue-100 text-blue-800"}`}>
                        {m.movementType}
                      </span>
                    </TableCell>
                    <TableCell>{m.itemName}</TableCell>
                    <TableCell className="text-right font-mono">{m.quantity.toLocaleString()} {m.unit}</TableCell>
                    <TableCell>{m.locationName}</TableCell>
                    <TableCell className="text-sm">{new Date(m.occurredAt).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
